import {ComponentRef, Type} from '@angular/core';
import {
    DynamicComponent,
    DynamicComponentOptions,
    NgDynamicComponentsService
} from '../../service/ng-dynamic-components.service';
import {Popup, PopupTrigger} from '@mescius/wijmo.input';
import {PopupPosition} from '@mescius/wijmo';


export type AfterTooltipHideHandler<T extends DynamicComponent> = (componentRef: T) => void | Promise<void>;

export interface ComponentTooltipOptions2<T extends DynamicComponent = any, D = any> {
    width?: string;
    height?: string;
    maxHeight?: string;
    maxWidth?: string;
    padding?: string;
    isResizable?: boolean
    isDraggable?: boolean;
}

/**
 * Interface representing the options for configuring a tooltip associated with a dynamic component.
 *
 * @template T The type of the dynamic component that the tooltip creates when showing up.
 * @template O The type of the input data used for initializing the tooltip (via ComponentRef.setInput()).
 */
interface EventWithTarget {
    target: HTMLElement;
}

/**
 * Represents a tooltip hook that can dynamically attach to HTML elements, providing component-based tooltips.
 * The tooltips are managed using mouse-over and mouse-out events and can be customized with a variety of options.
 * (Note - this implementation is not using Wijmo's Tooltip class but rather a custom implementation.)
 *
 * The type of the tooltip's content is determined by the generic type parameter `T`, which extends `DynamicComponent`.
 * The `O` generic parameter defines the type of the input options passed to the dynamic component.
 *
 * THIS IS NOT AN ANGULAR COMPONENT CLASS (despite its similar name). Therefore, it cannot make use
 * of component / service dependency injection (get services via constructor parameters).
 */
export class ComponentTooltip2 {

    // options for the tooltip (note that the 'input' option represents the options handed over to the component)
    options: ComponentTooltipOptions2;

    private currentPopup: Popup | undefined;

    constructor(private readonly dynCompSvc: NgDynamicComponentsService, options?: ComponentTooltipOptions2) {
        this.options = options ?? {};
    }

    async show<T extends DynamicComponent>(target: Element, componentType: Type<T>, init?: any, onHidden?: AfterTooltipHideHandler<T>): Promise<T> {
        this.cancelCurrentPopup();

        // Evaluate / assign init values and possibly cancel before showing the tooltip
        // const beforeShowResult = await this.options?.beforeShow?.call(undefined, this, target, init, regInfo?.data);
        // if (beforeShowResult === false) {
        //    return;
        // }

        const popupEl = document.createElement('div');
        popupEl.classList.add('tooltip-popup-container-hidden');
        popupEl.style.margin = '0 10px';
        popupEl.style.padding = '2px';

        const dynCompOpts: DynamicComponentOptions = {
            hostElement: popupEl,
            input: init
        }
        const componentRef = await this.dynCompSvc.createComponent(componentType, dynCompOpts);

        const popup = new Popup(popupEl, {
            owner: target,
            position: PopupPosition.RightTop,
            showTrigger: PopupTrigger.None,
            hideTrigger: PopupTrigger.Blur | PopupTrigger.LeavePopup | PopupTrigger.LeaveOwner | PopupTrigger.Leave,
            fadeIn: false,
            fadeOut: false,
            isResizable: this.options.isResizable ?? false,
            isDraggable: this.options.isDraggable ?? false,
            resizing: (p: Popup) => p.hideTrigger = PopupTrigger.Blur,
            hidden: async () => await this.onPopupHidden(componentRef, onHidden),
        });
        popup.hostElement.addEventListener('click', (e: MouseEvent) => popup.hideTrigger = PopupTrigger.Blur);
        popup.hostElement.style.width = this.options.width ?? 'auto';
        popup.hostElement.style.height = this.options.height ?? 'auto';
        popup.hostElement.style.maxHeight = this.options.maxHeight ?? '';
        popup.hostElement.style.maxWidth = this.options.maxWidth ?? '';
        popup.hostElement.style.overflow = 'auto';
        popup.hostElement.style.padding = this.options.padding ?? '0';
        this.currentPopup = popup;
        popup.show();

        return componentRef.instance;
    }

    private async onPopupHidden(componentRef: ComponentRef<any>, callBack?: AfterTooltipHideHandler<any>) {
        try {
            const returnValue = callBack?.call(undefined, componentRef.instance);
            if (returnValue instanceof Promise) {
                 await returnValue;
            }
        } finally {
            componentRef.destroy();
            this.currentPopup = undefined;
        }
    }

    private cancelCurrentPopup() {
        if (this.currentPopup) {
            this.currentPopup.hide();
            this.currentPopup = undefined;
        }
    }
}
